<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  subService: { type: Object, default: null },
  slas: { type: Array, default: () => [] },
  isSubServiceSaved: { type: Boolean, default: false },
});

const emit = defineEmits(['update:show', 'save', 'close']);

const { t } = useI18n();

const form = ref({
  name: '',
  description: '',
  sla_policy_id: '',
});

const errors = ref({});

const showModal = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

watch(
  () => props.subService,
  newValue => {
    if (newValue) {
      form.value = {
        name: newValue.name || '',
        description: newValue.description || '',
        sla_policy_id: newValue.sla_policy_id || '',
      };
    } else {
      form.value = {
        name: '',
        description: '',
        sla_policy_id: '',
      };
    }
    errors.value = {};
  },
  { immediate: true }
);

watch(
  () => props.show,
  isShowing => {
    // Quando o modal é fechado, limpa o formulário
    if (!isShowing) {
      form.value = {
        name: '',
        description: '',
        sla_policy_id: '',
      };
      errors.value = {};
    } else if (!props.subService) {
      // Quando o modal é aberto sem subService (novo), garante que está vazio
      form.value = {
        name: '',
        description: '',
        sla_policy_id: '',
      };
      errors.value = {};
    }
  }
);

const validate = () => {
  errors.value = {};
  let isValid = true;

  if (!form.value.name.trim()) {
    errors.value.name = t('SERVICES_SETTINGS.SUBSERVICE_FORM.NAME.ERROR');
    isValid = false;
  }

  // SLA só é obrigatório se o subserviço ainda não foi salvo
  if (!props.isSubServiceSaved && !form.value.sla_policy_id) {
    errors.value.sla_policy_id = t(
      'SERVICES_SETTINGS.SUBSERVICE_FORM.SLA.ERROR'
    );
    isValid = false;
  }

  return isValid;
};

const handleSave = () => {
  if (!validate()) {
    return;
  }

  emit('save', {
    ...form.value,
    id: props.subService?.id,
  });
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Modal v-model:show="showModal" :on-close="handleClose">
    <div class="p-8">
      <h2 class="text-xl font-medium mb-6 text-n-slate-12">
        {{
          subService
            ? $t('SERVICES_SETTINGS.SUBSERVICE_FORM.EDIT_TITLE')
            : $t('SERVICES_SETTINGS.SUBSERVICE_FORM.CREATE_TITLE')
        }}
      </h2>

      <form class="flex flex-col gap-6" @submit.prevent="handleSave">
        <Input
          v-model="form.name"
          :label="$t('SERVICES_SETTINGS.SUBSERVICE_FORM.NAME.LABEL')"
          :placeholder="
            $t('SERVICES_SETTINGS.SUBSERVICE_FORM.NAME.PLACEHOLDER')
          "
          :message="errors.name"
          :message-type="errors.name ? 'error' : 'info'"
          required
        />

        <TextArea
          v-model="form.description"
          :label="$t('SERVICES_SETTINGS.SUBSERVICE_FORM.DESCRIPTION.LABEL')"
          :placeholder="
            $t('SERVICES_SETTINGS.SUBSERVICE_FORM.DESCRIPTION.PLACEHOLDER')
          "
          resize
        />

        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ $t('SERVICES_SETTINGS.SUBSERVICE_FORM.SLA.LABEL') }}
            <span class="text-n-ruby-9">{{
              $t('SERVICES_SETTINGS.FORM.REQUIRED')
            }}</span>
          </label>
          <select
            v-model="form.sla_policy_id"
            :disabled="isSubServiceSaved"
            class="block w-full reset-base text-sm h-10 !mb-0 outline outline-1 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 border-none border-0 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
            :class="{
              'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9':
                errors.sla_policy_id,
              'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand':
                !errors.sla_policy_id,
            }"
          >
            <option value="" disabled>
              {{ $t('SERVICES_SETTINGS.SUBSERVICE_FORM.SLA.PLACEHOLDER') }}
            </option>
            <option v-for="sla in slas" :key="sla.id" :value="sla.id">
              {{ sla.name }}
            </option>
          </select>
          <p
            v-if="errors.sla_policy_id"
            class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
          >
            {{ errors.sla_policy_id }}
          </p>
          <p
            v-if="isSubServiceSaved"
            class="min-w-0 mt-1 mb-0 text-xs text-n-slate-11"
          >
            {{ $t('SERVICES_SETTINGS.SUBSERVICE_FORM.SLA.EDIT_DISABLED') }}
          </p>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <Button
            type="button"
            variant="faded"
            slate
            :label="$t('SERVICES_SETTINGS.SUBSERVICE_FORM.CANCEL')"
            @click="handleClose"
          />
          <Button
            type="submit"
            :label="$t('SERVICES_SETTINGS.SUBSERVICE_FORM.SAVE')"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>
